import "./utility-tools.css";
import { canvasBlob } from "./bmp-browser.js";
import { createOperationResult } from "./operation-result.js";
import { formatFileSize, safeBaseName } from "./utility-tools-core.js";

const root = document.querySelector("[data-compress-jpeg]");

if (root) {
  const ids = ["jpegStatus", "jpegUploadPanel", "jpegDropZone", "chooseJpeg", "jpegFileInput", "jpegWorkspace", "jpegPreview", "jpegFileName", "jpegOriginalSize", "jpegQuality", "jpegQualityValue", "compressJpeg", "replaceJpeg"];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { file: null, bitmap: null, previewUrl: null, busy: false };
  const result = createOperationResult(root, elements.jpegWorkspace);

  function status(message = "") { elements.jpegStatus.textContent = message; elements.jpegStatus.hidden = !message; }
  function controls() { elements.compressJpeg.disabled = state.busy || !state.bitmap; elements.replaceJpeg.disabled = state.busy; elements.jpegQuality.disabled = state.busy; }

  async function load(file) {
    if (!file || state.busy) return;
    status();
    state.busy = true;
    controls();
    try {
      if (file.size > 25 * 1024 * 1024) throw new Error("JPEG files are limited to 25 MB.");
      const header = new Uint8Array(await file.slice(0, 3).arrayBuffer());
      if (header[0] !== 0xff || header[1] !== 0xd8 || header[2] !== 0xff) throw new Error("File content is not a valid JPEG image.");
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      if (bitmap.width * bitmap.height > 64_000_000) { bitmap.close(); throw new Error("Decoded image exceeds the 64 megapixel limit."); }
      state.bitmap?.close();
      state.bitmap = bitmap;
      state.file = file;
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      state.previewUrl = URL.createObjectURL(file);
      elements.jpegPreview.src = state.previewUrl;
      elements.jpegFileName.textContent = `${file.name} · ${bitmap.width} × ${bitmap.height} px`;
      elements.jpegOriginalSize.textContent = `Original size: ${formatFileSize(file.size)}`;
      elements.jpegUploadPanel.hidden = true;
      elements.jpegWorkspace.hidden = false;
    } catch (error) {
      status(error?.message || "This JPEG image could not be opened.");
    } finally {
      state.busy = false;
      elements.jpegFileInput.value = "";
      controls();
    }
  }

  async function compress() {
    if (!state.file || !state.bitmap || state.busy) return;
    status();
    state.busy = true;
    controls();
    try {
      const canvas = document.createElement("canvas");
      canvas.width = state.bitmap.width;
      canvas.height = state.bitmap.height;
      canvas.getContext("2d", { alpha: false }).drawImage(state.bitmap, 0, 0);
      const blob = await canvasBlob(canvas, "image/jpeg", Number(elements.jpegQuality.value) / 100);
      if (blob.size >= state.file.size) throw new Error(`This setting produced ${formatFileSize(blob.size)}, which is not smaller than the original. Choose a lower quality.`);
      const reduction = Math.round((1 - blob.size / state.file.size) * 100);
      result.show({
        blob,
        fileName: `${safeBaseName(state.file.name, "compressed")}-compressed.jpg`,
        message: "Your smaller JPEG is ready.",
        details: [`${formatFileSize(state.file.size)} → ${formatFileSize(blob.size)}`, `${reduction}% smaller`]
      });
    } catch (error) {
      status(error?.message || "The compressed JPEG could not be created.");
    } finally {
      state.busy = false;
      controls();
    }
  }

  const choose = () => elements.jpegFileInput.click();
  elements.chooseJpeg.addEventListener("click", event => { event.stopPropagation(); choose(); });
  elements.replaceJpeg.addEventListener("click", choose);
  elements.jpegFileInput.addEventListener("change", () => load(elements.jpegFileInput.files?.[0]));
  elements.compressJpeg.addEventListener("click", compress);
  elements.jpegQuality.addEventListener("input", () => { elements.jpegQualityValue.textContent = `${elements.jpegQuality.value}%`; });
  elements.jpegDropZone.addEventListener("click", event => { if (!event.target.closest("button")) choose(); });
  elements.jpegDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); choose(); } });
  elements.jpegDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.jpegDropZone.classList.add("is-dragging"); });
  elements.jpegDropZone.addEventListener("dragleave", () => elements.jpegDropZone.classList.remove("is-dragging"));
  elements.jpegDropZone.addEventListener("drop", event => { event.preventDefault(); elements.jpegDropZone.classList.remove("is-dragging"); load(event.dataTransfer.files?.[0]); });
  window.addEventListener("beforeunload", () => { result.dispose(); state.bitmap?.close(); if (state.previewUrl) URL.revokeObjectURL(state.previewUrl); });
}
